import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type {
  HookParams,
  UpdateProfilePicPayload,
  UploadListData,
  UploadListParams,
  UploadPayload,
} from '@/libs/interfaces'
import { uploadsApi } from '@/services/api'

export function useUploads({ params, enabled }: HookParams<UploadListParams>) {
  return useQuery<UploadListData>({
    queryKey: QUERY_KEYS.uploads.list(params),
    queryFn: () => uploadsApi.list(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useUploadPreSignedUrl({
  params,
  enabled,
}: HookParams<{ orgID: string; uploadID: string }>) {
  return useQuery<string>({
    queryKey: QUERY_KEYS.uploads.preSignedUrl(params.uploadID),
    queryFn: () => uploadsApi.getPreSignedUrl(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useUploadFile() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (payload: UploadPayload) => uploadsApi.upload(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.uploads.all })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useUploadDelete() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (params: { orgID: string; uploadID: string }) => uploadsApi.delete(params),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.uploads.all })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useUploadUpdateProfilePic() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (payload: UpdateProfilePicPayload) => uploadsApi.updateProfilePic(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.uploads.all })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}
